import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Analytics loader — GA4 + Meta Pixel.
 *
 * Behavior:
 * - Only loads when env vars are set; otherwise no-op (safe in dev).
 * - Loads scripts ONCE on first render.
 * - Tracks SPA route changes (pageview events) on every location change.
 * - Skips loading on /admin/** to avoid tracking internal use.
 *
 * Env vars (set in Railway / .env.local):
 *   REACT_APP_GA4_ID         e.g. "G-XXXXXXXXXX"
 *   REACT_APP_META_PIXEL_ID  e.g. "123456789012345"
 */

const GA4_ID = process.env.REACT_APP_GA4_ID;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const ANALYTICS_DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

let ga4Loaded = false;
let pixelLoaded = false;

function shouldSkipPath(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith('/admin')) return true;
  return false;
}

function loadGA4() {
  if (ga4Loaded || !GA4_ID || typeof window === 'undefined') return;
  ga4Loaded = true;

  // dataLayer + gtag stub
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA4_ID, {
    send_page_view: false, // we'll send manually on route change
    anonymize_ip: true,
  });

  // Inject gtag.js
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);

  if (ANALYTICS_DEBUG) console.info('[analytics] GA4 loaded:', GA4_ID);
}

function loadMetaPixel() {
  if (pixelLoaded || !META_PIXEL_ID || typeof window === 'undefined') return;
  pixelLoaded = true;

  /* eslint-disable */
  // Standard Meta Pixel snippet (https://developers.facebook.com/docs/meta-pixel/get-started)
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', META_PIXEL_ID);

  if (ANALYTICS_DEBUG) console.info('[analytics] Meta Pixel loaded:', META_PIXEL_ID);
}

function trackPageview(pathname, search) {
  const path = pathname + (search || '');

  if (GA4_ID && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: document.title,
    });
  }

  if (META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }

  if (ANALYTICS_DEBUG) console.info('[analytics] pageview:', path);
}

const Analytics = () => {
  const location = useLocation();

  // Initial mount: load scripts (skip on admin routes)
  useEffect(() => {
    if (shouldSkipPath(location.pathname)) return;
    loadGA4();
    loadMetaPixel();
    // Track the first pageview after scripts are queued
    trackPageview(location.pathname, location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subsequent route changes
  useEffect(() => {
    if (shouldSkipPath(location.pathname)) return;
    trackPageview(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return null;
};

export default Analytics;
